import { useCallback, useEffect, useRef, useState } from 'react'
import api from '../api/client'
import Boton from './Boton'
import { colors, radius, space, sx, text } from '../design/tokens'
import { fmtFechaHora } from '../utils/fecha'

// Conectar la 6060 de Ventas a LGR por COEXISTENCIA (1 oct 2026): el número
// sigue en la app del celular. El botón abre el Embedded Signup de Meta; al
// terminar, LGR cambia el código por el token, suscribe la app y pide contactos
// e historial en el acto (Meta lo permite una sola vez y dentro de las 24 h).
// Solo el super administrador.

const SDK = 'https://connect.facebook.net/es_LA/sdk.js'

function cargarSdk(appId, version) {
  return new Promise((resolve, reject) => {
    if (window.FB) { resolve(window.FB); return }
    window.fbAsyncInit = () => {
      window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version })
      resolve(window.FB)
    }
    const s = document.createElement('script')
    s.src = SDK
    s.async = true
    s.crossOrigin = 'anonymous'
    s.onerror = () => reject(new Error('No se pudo cargar el SDK de Meta.'))
    document.body.appendChild(s)
  })
}

const TONO_GRIS = { background: colors.bgSoft, color: colors.textSub }
const TONO_HISTORIAL = {
  pedido: { background: colors.linkBg, color: colors.link },
  recibiendo: { background: colors.linkBg, color: colors.link },
  completo: { background: colors.okBg, color: colors.ok },
  rechazado: { background: colors.errBg, color: colors.err },
}

const ESTADO_HISTORIAL = {
  pedido: 'Pedido a Meta, esperando que llegue',
  recibiendo: 'Llegando',
  completo: 'Completo',
  rechazado: 'No compartido desde la app',
}

export default function ConexionVentasWhatsApp() {
  const [datos, setDatos] = useState(null)
  const [error, setError] = useState(null)
  const [conectando, setConectando] = useState(false)
  const [resultado, setResultado] = useState(null)
  const sesion = useRef({ wabaId: null, phoneNumberId: null })

  const cargar = useCallback(async () => {
    try { setDatos((await api.get('/whatsapp-coexistencia')).data) } catch { setError('No se pudo leer el estado de la conexión.') }
  }, [])
  useEffect(() => { cargar() }, [cargar])

  // Meta manda los ids de la cuenta por postMessage cuando termina el flujo.
  useEffect(() => {
    const escuchar = (event) => {
      if (!String(event.origin).endsWith('facebook.com')) return
      try {
        const d = JSON.parse(event.data)
        if (d.type !== 'WA_EMBEDDED_SIGNUP') return
        if (d.event === 'CANCEL') { setError(d.data?.error_message ? `Meta: ${d.data.error_message}` : 'Se cerró la ventana de Meta antes de terminar.'); return }
        sesion.current = { wabaId: d.data?.waba_id ?? null, phoneNumberId: d.data?.phone_number_id ?? null }
      } catch { /* mensajes de Meta que no son del registro */ }
    }
    window.addEventListener('message', escuchar)
    return () => window.removeEventListener('message', escuchar)
  }, [])

  async function conectar() {
    setError(null); setResultado(null); setConectando(true)
    try {
      const FB = await cargarSdk(datos.app_id, datos.version)
      // FB.login exige una función común (no async): rechaza "asyncfunction".
      FB.login((response) => { terminar(response) }, {
        config_id: datos.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: 'whatsapp_business_app_onboarding', sessionInfoVersion: '3' },
      })
    } catch (e) {
      setError(e.message)
      setConectando(false)
    }
  }

  async function terminar(response) {
    const code = response?.authResponse?.code
    // El postMessage con los ids puede llegar apenas después del callback.
    for (let i = 0; i < 20 && !sesion.current.wabaId; i++) await new Promise(r => setTimeout(r, 250))
    if (!code || !sesion.current.wabaId) {
      setError('Meta no devolvió la conexión (se canceló o no terminó el registro). No se cambió nada.')
      setConectando(false)
      return
    }
    try {
      const r = await api.post('/whatsapp-coexistencia/conectar', { code, waba_id: sesion.current.wabaId, phone_number_id: sesion.current.phoneNumberId })
      setResultado(r.data.pasos)
    } catch (e) {
      setError(e?.response?.data?.message || 'LGR no pudo completar la conexión.')
    } finally {
      setConectando(false)
      cargar()
    }
  }

  async function reintentar(id) {
    setError(null)
    try { setResultado((await api.post(`/whatsapp-coexistencia/${id}/sincronizar`)).data.pasos) } catch (e) { setError(e?.response?.data?.message || 'No se pudo reintentar.') }
    cargar()
  }

  if (!datos) return null
  const conectadas = datos.lineas.filter(l => l.coexistencia)

  return (
    <div style={{ ...sx.card, marginBottom: space.xl }}>
      <div style={sx.cardTitle}>WhatsApp de Ventas (coexistencia)</div>
      <p style={st.nota}>Conecta la 6060 a LGR sin sacarla del celular: Ventas sigue usando la app y LGR recibe los últimos 6 meses de chats y todo lo nuevo. La línea queda atendida por personas (sin agente). Al terminar, LGR pide contactos e historial en el acto: Meta lo permite una sola vez.</p>

      {!datos.configurado && <div style={st.aviso}>Falta configurar en el servidor <span style={sx.mono}>WHATSAPP_APP_ID</span> y <span style={sx.mono}>WHATSAPP_ES_CONFIG_ID</span>.</div>}
      {error && <div role="alert" style={st.error}>{error}</div>}
      {resultado && resultado.length > 0 && <ul style={st.pasos}>{resultado.map((p, i) => <li key={i}>✓ {p}</li>)}</ul>}

      {conectadas.map(l => (
        <div key={l.id} style={st.linea}>
          <div><strong>{l.nombre}</strong> <span style={sx.mono}>{l.numero_visible}</span> · conectada <span style={sx.mono}>{fmtFechaHora(l.conectada_at)}</span></div>
          <div style={st.chips}>
            <span style={{ ...sx.badge, ...(TONO_HISTORIAL[l.historial_estado] ?? TONO_GRIS) }}>
              Historial: {ESTADO_HISTORIAL[l.historial_estado] ?? 'sin pedir'}{l.historial_progreso != null && ` · ${l.historial_progreso} %`}
            </span>
            <span style={{ ...sx.badge, ...TONO_GRIS }}>Contactos: {l.contactos_pedido_at ? 'pedidos' : 'sin pedir'}</span>
            <span style={st.meta}>{Number(l.historial_mensajes).toLocaleString('es-AR')} mensajes recibidos</span>
          </div>
          {l.sync_error && <div style={st.error}>{l.sync_error}</div>}
          {(!l.contactos_pedido_at || !l.historial_pedido_at) && <Boton sm variante="secondary" onClick={() => reintentar(l.id)}>Reintentar sincronización</Boton>}
        </div>
      ))}

      <div style={{ marginTop: space.lg }}>
        <Boton variante="primary" disabled={!datos.configurado || conectando} onClick={conectar}>
          {conectando ? 'Conectando…' : conectadas.length ? 'Conectar otro WhatsApp de Ventas' : 'Conectar WhatsApp de Ventas'}
        </Boton>
      </div>
      {conectadas.length > 0 && (
        <p style={st.nota}>Cada número se conecta por separado y queda como su propia línea. Para volver a conectar uno que ya está, primero hay que desconectarlo desde la app del celular (Configuración → Cuenta → Plataforma comercial).</p>
      )}
    </div>
  )
}

const st = {
  nota: { margin: `${space.sm}px 0 0`, color: colors.textSec, fontSize: text.sec, lineHeight: 1.6 },
  aviso: { background: colors.warnBg, color: colors.warn, border: `1px solid ${colors.warnBorder}`, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, fontSize: text.sec, marginTop: space.md },
  error: { background: colors.errBg, color: colors.err, border: `1px solid ${colors.errBorder}`, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, fontSize: text.sec, marginTop: space.md },
  pasos: { margin: `${space.md}px 0 0`, listStyle: 'none', paddingLeft: 0, color: colors.ok, fontSize: text.sec, lineHeight: 1.7 },
  chips: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: space.md },
  meta: { color: colors.textSec, fontSize: text.sec },
  linea: { display: 'grid', gap: space.sm, justifyItems: 'start', marginTop: space.lg, paddingTop: space.lg, borderTop: `1px solid ${colors.borderSoft}`, fontSize: text.body, color: colors.text },
}
